/**
 * Wraps the shekel symbol (₪) in <nis> for styling.
 */
(function () {
  'use strict';

  const NIS_SYMBOL = '₪';

  /**
   * @param {Node} node
   * @returns {boolean} Whether this text node should be split and wrapped.
   */
  function shouldProcessTextNode(node) {
    if (!node || node.nodeType !== Node.TEXT_NODE) return false;

    const value = node.nodeValue;
    if (!value || !value.includes(NIS_SYMBOL)) return false;


    // check if the node is NOT inside a <nis> element
    let el = node.parentElement;
    while (el) {
      const tag = el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'nis') return false;
      if (tag === 'script' || tag === 'style' || tag === 'noscript' || tag === 'textarea' || tag === 'template') {
        return false;
      }
      el = el.parentElement;
    }

    return true;
  }

  /**
   * Replaces one text node with plain text + <nis>₪</nis> segments for each symbol.
   * @param {Text} textNode
   */
  function wrapNisInTextNode(textNode) {
    const fullText = textNode.nodeValue;
    if (!fullText || !fullText.includes(NIS_SYMBOL)) return;

    const parent = textNode.parentNode;
    if (!parent) return;

    // Replaces one text node with: [plain text][<nis>₪</nis>][plain text]…
    const fragment = document.createDocumentFragment();
    const symbolRegex = new RegExp(NIS_SYMBOL, 'g');
    let match;
    // Next slice of plain text starts here (right after the previous ₪, or 0 at the start).
    let plainSegmentStart = 0;

    while ((match = symbolRegex.exec(fullText)) !== null) {
      const symbolStart = match.index;
      const afterSymbol = symbolStart + match[0].length;

      // Characters before this ₪ stay a normal text node (e.g. "100 " in "100 ₪").
      if (symbolStart > plainSegmentStart) {
        fragment.appendChild(
          document.createTextNode(fullText.slice(plainSegmentStart, symbolStart)),
        );
      }

      const nisEl = document.createElement('nis');
      nisEl.appendChild(document.createTextNode(NIS_SYMBOL));
      fragment.appendChild(nisEl);

      plainSegmentStart = afterSymbol;
    }

    // Tail after the last ₪ (e.g. " incl. VAT").
    if (plainSegmentStart < fullText.length) {
      fragment.appendChild(document.createTextNode(fullText.slice(plainSegmentStart)));
    }

    parent.replaceChild(fragment, textNode);
  }

  /**
   * Walks `root` for matching text nodes. Collects nodes first, then mutates, so the walker is not invalidated mid-pass.
   * @param {ParentNode} root
   */
  function wrapNisInRoot(root) {
    if (!root) return;

    // Creates a tree walker to traverse the DOM and find text nodes to wrap.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        // If the node should be wrapped
        return shouldProcessTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    const textNodesToWrap = [];
    let current;
    while ((current = walker.nextNode())) {
      textNodesToWrap.push(current);
    }

    for (let i = 0; i < textNodesToWrap.length; i++) {
      wrapNisInTextNode(textNodesToWrap[i]);
    }
  }

  /**
   * Maps a mutation-added node to element root
   * @param {Node} node
   * @returns {Element[]}
   */
  function addedNodeToScanRoots(node) {
    // If the node is an element, return it
    if (node.nodeType === Node.ELEMENT_NODE) {
      return [node];
    }
    // If the node is a text node, return its parent element
    if (node.nodeType === Node.TEXT_NODE) {
      const p = node.parentElement;
      return p ? [p] : [];
    }
    // If the node is a document fragment, return its child elements
    if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      const out = [];
      for (let i = 0; i < node.childNodes.length; i++) {
        const c = node.childNodes[i];
        if (c.nodeType === Node.ELEMENT_NODE) out.push(c);
      }
      return out;
    }
    return [];
  }

  /** @param {Element[]} roots */
  function dropRootsNestedInAnother(roots) {
    if (roots.length <= 1) return roots;
    return roots.filter(function (el) {
      return !roots.some(function (ancestor) {
        return ancestor !== el && ancestor.contains(el);
      });
    });
  }

  /**
   * Inserted a fragment of several siblings? Scan their parent once instead of each sibling.
   * @param {Element[]} roots
   */
  function mergeSiblingsUnderSameParent(roots) {
    if (roots.length <= 1) return roots;

    const siblingCountByParent = new Map();
    for (let i = 0; i < roots.length; i++) {
      const parent = roots[i].parentElement;
      if (!parent) continue;
      siblingCountByParent.set(parent, (siblingCountByParent.get(parent) || 0) + 1);
    }

    const parentAlreadyQueued = new Set();
    const result = [];

    for (let i = 0; i < roots.length; i++) {
      const el = roots[i];
      const parent = el.parentElement;

      if (!parent) {
        result.push(el);
        continue;
      }

      const siblingsInBatch = siblingCountByParent.get(parent) || 0;
      if (siblingsInBatch <= 1) {
        result.push(el);
        continue;
      }

      if (!parentAlreadyQueued.has(parent)) {
        parentAlreadyQueued.add(parent);
        result.push(parent);
      }
    }

    return result;
  }

  /**
   * Turn a raw list of inserted element roots into a minimal set of subtrees to walk.
   * Order: dedupe → drop inner roots → merge sibling batches → drop inner roots again (after parent promotion).
   */
  function compactScanRoots(roots) {
    let next = Array.from(new Set(roots));
    next = dropRootsNestedInAnother(next);
    next = mergeSiblingsUnderSameParent(next);
    next = dropRootsNestedInAnother(next);
    return next;
  }

  function init() {
    wrapNisInRoot(document.body);

    if (typeof MutationObserver === 'undefined') return;

    let rafScheduled = false;
    let pendingRoots = [];

    function flushPendingRoots() {
      rafScheduled = false;
      const batch = pendingRoots;
      pendingRoots = [];

      for (const root of compactScanRoots(batch)) {
        if (root.isConnected) wrapNisInRoot(root);
      }
    }

    function queueSubtreeScans(roots) {
      pendingRoots.push(...roots);
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(flushPendingRoots);
    }

    new MutationObserver(function (mutations) {
      const roots = [];
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (m.type !== 'childList' || m.addedNodes.length === 0) continue;
        for (let j = 0; j < m.addedNodes.length; j++) {
          roots.push(...addedNodeToScanRoots(m.addedNodes[j]));
        }
      }
      if (roots.length) queueSubtreeScans(roots);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
